import { ClubService } from "src/app/services/ClubService";
import { environment } from "src/environments/environment";

/**
 * The logged-in user's club (tenant) context. homeTeamId replaces the old
 * hardcoded teamId === 1 convention; it defaults to 1 so everything keeps
 * working against a pre-multi-tenant API (Club/My missing) and on the
 * anonymous scoreboard screens until Phase 2 board-key resolution.
 */
export const ClubContext = {
	club: null as any,
	/**
	 * Which products this club has, keyed by feature name. Rows in the database
	 * rather than columns on the club, so adding a feature is an INSERT not a
	 * migration — which means it arrives as its own call.
	 *
	 * Empty until loaded. Callers should treat a missing key as off, except the
	 * scoreboard, which the API defaults on for clubs that predate the flags.
	 */
	features: {} as { [key: string]: boolean },
	homeTeamId: 1,
	loaded: false
};

/**
 * Data access is decided by the ClubId claim in the JWT, not the URL - the
 * slug in /my-club/{slug} is the club's public identity (branded URLs, the
 * mobile Account ID). Route links are built from environment.build.adminRoute,
 * so pointing that at the slugged base updates every Link/Navigate in the app.
 */
export function SetAdminRoute(slug: string) {
	const slugLower = String(slug || "").toLowerCase();
	if (!slugLower || slugLower === "home") return;
	environment.build.adminRoute = `my-club/${slugLower}`;
}

/** Adopt the slug from a /my-club/{slug}/... URL the user landed on. */
export function SetAdminRouteFromLocation() {
	const match = window.location.pathname.match(/^\/my-club\/([^/]+)/);
	if (match) SetAdminRoute(match[1]);
}

export async function LoadClubContext() {
	try {
		const res = await ClubService.getMy();
		if (!res.ok) return;
		const club = await res.json();
		if (club && club.id) {
			ClubContext.club = club;
			// Which products this club has. Rows in the database now, so it's a
			// separate call rather than fields on the club.
			try {
				const featuresRes = await ClubService.getFeatures();
				if (featuresRes.ok) ClubContext.features = await featuresRes.json();
			} catch { /* defaults apply — see the API's ClubFeatures */ }
			ClubContext.homeTeamId = Number(club.homeTeamId) || 1;
			ClubContext.loaded = true;
			if (club.slug) {
				SetAdminRoute(club.slug);
				// Correct the address bar if the URL slug isn't the club's real one.
				const path = window.location.pathname;
				const corrected = path.replace(/^\/my-club\/[^/]+/, `/${environment.build.adminRoute}`);
				if (corrected !== path) window.history.replaceState(null, "", corrected + window.location.search);
			}
		}
	} catch { /* pre-multi-tenant API - defaults stand */ }
}

/**
 * Whether a club feature is on.
 *
 * Case-insensitive on purpose. The API defines its keys in PascalCase
 * ("Ticketing") and the JSON serialiser emits camelCase ("ticketing"), so a
 * direct property read silently returns undefined — which reads as "off" and
 * would quietly hide a feature the club is paying for.
 *
 * `defaultWhenMissing` matters: the API defaults the scoreboard ON for clubs
 * that predate the flags, and this has to agree with it.
 */
export function HasFeature(key: string, defaultWhenMissing = false): boolean {
	const features = ClubContext.features || {};
	const match = Object.keys(features).find((k) => k.toLowerCase() === key.toLowerCase());
	return match ? !!features[match] : defaultWhenMissing;
}
